import { Mascot } from "../components/Mascot";
import { MASCOTS } from "../components/mascots";
import { Shot } from "../components/Shot";
import { SHOTS } from "../components/shots";
import { StoreMark, type StorePlatform } from "../components/StoreMark";
import { useHasKey } from "../components/useHasKey";
import { resolveConfig, type SiteConfig } from "../config";
import { useTranslation } from "react-i18next";

/**
 * The two app stores, in the order the platforms section lists them, each paired with the field
 * of the config that holds its address.
 */
const STORES: readonly { platform: StorePlatform; url: keyof SiteConfig }[] = [
  { platform: "ios", url: "iosUrl" },
  { platform: "android", url: "androidUrl" },
];

/**
 * The first screen is the product itself.
 *
 * An earlier draft led with a diagram of what the server stores. The owner rejected it, and
 * the reason is worth keeping written down: an argument that has to be explained does not
 * work as an opening. A visitor should recognise a messenger before being asked to follow a
 * claim about one (landing/todo/40-visual-direction.md §1.1).
 *
 * THE FRAME IS A REAL SCREENSHOT, NOT A DRAWING. The approved mockup builds a fake app out
 * of divs — a rail, a chat list, four bubbles. This uses the captured `chat-desktop` frame
 * inside that same shell instead, and the choice is deliberate: the owner had the frames
 * taken for this page, a photograph of the product cannot quietly drift out of date the way
 * an imitation does, and a page whose whole argument is "check us rather than trust us"
 * should not open on a picture of a messenger that does not exist. The shell around it —
 * outline, radius, title bar, deep shadow — is the mockup's, so the look survives.
 *
 * This is the only eagerly loaded image on the page.
 */
export function Hero({ config }: { config: SiteConfig }) {
  const { t } = useTranslation();
  const has = useHasKey();
  const urls = resolveConfig(config);

  return (
    <section className="hero">
      <div className="hero__text">
        <h1 className="hero__heading">{t("home.heading")}</h1>
        <p className="hero__lede">{t("home.lede")}</p>

        <div className="hero__actions">
          {/* Same destination as the Web card in the platforms section, so it behaves the
              same way: new tab, and `noopener` because without it the opened page keeps a
              live handle on this window and can navigate it (tabnabbing). The hidden note
              borrows the platforms key rather than inventing a second one — the string is
              about the behaviour, not about the section it lives in. */}
          <a
            className="button button--primary"
            href={config.webAppUrl}
            target="_blank"
            rel="noopener noreferrer"
          >
            {t("home.ctaPrimary")}
            <span className="sr-only"> {t("platforms.opensInNewTab")}</span>
          </a>
          {/* Points at the limits section, so it is only offered while that section has
              something to show — an in-page link to a heading that no longer renders is a
              dead end baked into static HTML. */}
          {has("home.ctaSecondary") && has("limits.heading") ? (
            <a className="button button--quiet" href="#limits">
              {t("home.ctaSecondary")}
            </a>
          ) : null}
        </div>

        {/* The two apps, offered in the first screen rather than only seven sections down
            (owner's request, 2026-09-01). They are a SECOND row, quieter than the primary
            button: the web app is still the one action the page pushes, because it needs no
            install at all — these are for the visitor who already knows they want the phone.

            THE LABELS ARE THE PLATFORMS SECTION'S OWN KEYS, deliberately. Reusing
            `platforms.<platform>.action` means the hero says exactly what the card further down
            says, in all 24 locales, without adding a key that would render as its own name in
            the 23 dictionaries a new English string had not reached yet. The same `has` guard
            applies for the same reason it does everywhere else on this page: a dropped key
            removes the link rather than baking a placeholder into static HTML.

            `resolveConfig`, not `config`, so an unset build argument falls back to the real
            store address instead of an empty href — the same treatment the platforms cards
            give it. */}
        <div className="hero__stores">
          {STORES.map(({ platform, url }) =>
            has(`platforms.${platform}.action`) ? (
              <a
                key={platform}
                className="button button--quiet button--store"
                href={urls[url]}
                target="_blank"
                rel="noopener noreferrer"
              >
                <StoreMark platform={platform} />
                {t(`platforms.${platform}.action`)}
                <span className="sr-only"> {t("platforms.opensInNewTab")}</span>
              </a>
            ) : null,
          )}
        </div>

        {/* A caveat about the product's state belongs next to the button that leads into it,
            not in a footnote nobody scrolls to — and it disappears with its key, because the
            caveat is temporary and the hero must not keep an empty line where it was. */}
        {has("home.ctaPrimaryNote") ? (
          <p className="hero__note">{t("home.ctaPrimaryNote")}</p>
        ) : null}
      </div>

      <div className="hero__frame">
        <div className="appframe">
          {/* Window furniture, drawn rather than photographed: the screenshots were cropped
              to the interface, and without a title bar above them the frame read as a flat
              picture pasted onto the page instead of an application. Decorative, so it is
              hidden from assistive technology — the <img> alt already says what this is. */}
          <div className="appframe__bar" aria-hidden="true">
            <span className="appframe__dot" />
            <span className="appframe__dot" />
            <span className="appframe__dot" />
            {/* The product's own name, not a sentence: it needs no translation. */}
            <span className="appframe__label">{t("site.name")}</span>
          </div>
          <Shot shot={SHOTS.chatDesktop} altKey="shots.chatDesktop.alt" priority />
        </div>

        {/* Stands at the frame's bottom-RIGHT corner and leans onto it — see .hero__mascot
            for why that corner and nothing else. Rendered after .appframe so it stacks above
            the window without needing to fight anyone for a z-index. Eager: it is in the first
            screen, and a decorative figure that fades in late is worse than one that is
            simply there. */}
        <Mascot className="hero__mascot" mascot={MASCOTS.stand} priority />
      </div>
    </section>
  );
}
